import React, { useState, useMemo, useRef } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  IconButton, 
  Divider, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid, 
  FormControlLabel, 
  Switch, 
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { 
  Home as HomeIcon, 
  ChevronRight, 
  ViewCarousel, 
  Add, 
  Edit, 
  Delete, 
  Close,
  CheckCircle,
  Cancel,
  Sync,
  CloudUpload
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { log } from '../services/loggingService';
import { syncTechyShopBanners } from '../services/bannerService';

function TechyShopBanners() {
  const navigate = useNavigate();
  
  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  
  // Banner form state
  const [formData, setFormData] = useState({
    id: '',
    titolo: '',
    sottotitolo: '',
    immagine: '',
    link: '',
    testo_bottone: '',
    posizione: '',
    attivo: true,
    ordinamento: 0,
    data_inizio: '',
    data_fine: '',
    created_at: ''
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  // Ottieni banner in tempo reale dal DB
  const banners = useLiveQuery(() => db.techyshop_banner.toArray(), []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsSaving(true);
    setToast({ open: true, message: 'Caricamento immagine in corso...', severity: 'info' });

    const uploadFormData = new FormData();
    uploadFormData.append('immagine', file);
    uploadFormData.append('tabella', 'techyshop_banner');

    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/addImmagine', {
        method: 'POST',
        headers: {
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: uploadFormData,
      });

      const result = await response.json();

      if (result.result && result.details) {
        const newImagePath = result.details;
        
        // Aggiorna lo stato del form
        setFormData(prev => ({
          ...prev,
          immagine: newImagePath
        }));

        // Se stiamo modificando un banner esistente, aggiorna anche Dexie
        if (selectedBanner) {
          await db.techyshop_banner.update(selectedBanner.id, { immagine: newImagePath });
          setToast({ open: true, message: 'Immagine caricata e salvata con successo!', severity: 'success' });
        } else {
          setToast({ open: true, message: 'Immagine caricata correttamente!', severity: 'success' });
        }
      } else {
        throw new Error(result.text || 'Errore durante l\'upload dell\'immagine');
      }
    } catch (error) {
      log("Errore nell'upload dell'immagine banner:", 'ERROR', error);
      setToast({ open: true, message: `Errore upload: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
      // Reset dell'input file per permettere di selezionare lo stesso file nuovamente
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleSync = async () => {
    setIsSaving(true);
    setToast({ open: true, message: 'Sincronizzazione in corso...', severity: 'info' });
    
    try {
      const result = await syncTechyShopBanners(true);
      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      log("Errore durante la sincronizzazione manuale banner:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenModal = (banner = null) => {
    if (banner) {
      setSelectedBanner(banner);
      setFormData({
        id: banner.id || '',
        titolo: banner.titolo || '',
        sottotitolo: banner.sottotitolo || '',
        immagine: banner.immagine || '',
        link: banner.link || '',
        testo_bottone: banner.testo_bottone || '',
        posizione: banner.posizione || '',
        attivo: banner.attivo === 1 || banner.attivo === true,
        ordinamento: banner.ordinamento || 0,
        data_inizio: banner.data_inizio || '',
        data_fine: banner.data_fine || '',
        created_at: banner.created_at || ''
      });
    } else {
      setSelectedBanner(null);
      setFormData({
        id: '',
        titolo: '',
        sottotitolo: '',
        immagine: '',
        link: '',
        testo_bottone: '',
        posizione: '',
        attivo: true,
        ordinamento: 0,
        data_inizio: '',
        data_fine: '',
        created_at: new Date().toISOString()
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBanner(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveBanner = async () => {
    if (!formData.titolo.trim()) {
      setToast({ open: true, message: 'Errore: Il titolo è obbligatorio.', severity: 'error' });
      return;
    }

    setIsSaving(true);
    
    const bannerData = {
      ...formData,
      attivo: formData.attivo ? 1 : 0,
      ordinamento: parseInt(formData.ordinamento) || 0,
    };

    // Formattazione data per MySQL (YYYY-MM-DD HH:MM:SS)
    const now = new Date();
    const mysqlTimestamp = now.toISOString().slice(0, 19).replace('T', ' ');

    try {
      if (selectedBanner) {
        // Prepariamo i valori da modificare: solo quelli che sono cambiati
        const values = [];
        
        // Campi da controllare per le modifiche
        const fieldsToTrack = [
          'titolo', 'sottotitolo', 'immagine', 'link', 
          'testo_bottone', 'posizione', 'attivo', 
          'ordinamento', 'data_inizio', 'data_fine'
        ];

        fieldsToTrack.forEach(field => {
          let newValue = formData[field];
          let oldValue = selectedBanner[field];

          // Normalizzazione per il confronto
          if (field === 'attivo') {
            newValue = newValue ? 1 : 0;
            oldValue = (oldValue === 1 || oldValue === true) ? 1 : 0;
          } else if (field === 'ordinamento') {
            newValue = parseInt(newValue) || 0;
            oldValue = parseInt(oldValue) || 0;
          } else {
            // Per gli altri campi (stringhe), gestiamo null/undefined
            newValue = newValue || '';
            oldValue = oldValue || '';
          }

          if (newValue !== oldValue) {
            values.push({ field, value: newValue });
          }
        });

        // Se non è cambiato nulla, non facciamo la chiamata
        if (values.length === 0) {
          handleCloseModal();
          setIsSaving(false);
          return;
        }

        const filters = [{ field: 'id', value: selectedBanner.id }];

        const response = await fetch('https://deadapi.techysumo.it/techyshop/editBanner', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({ values, filters }),
        });

        const result = await response.json();

        if (result.result) {
          await db.techyshop_banner.update(selectedBanner.id, bannerData);
          setToast({ open: true, message: 'Banner aggiornato con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante l'aggiornamento del banner sul server");
        }
      } else {
        // Logica di aggiunta con chiamata API
        const { id, ...rawPayload } = bannerData;
        rawPayload.created_at = mysqlTimestamp;
        
        // Filtriamo i valori vuoti dal payload
        const payload = Object.fromEntries(
          Object.entries(rawPayload).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
        );
        
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addBanner', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (result.result) {
          const newId = result.details?.id || result.details || Date.now();
          await db.techyshop_banner.add({ ...bannerData, id: newId });
          setToast({ open: true, message: 'Banner creato e sincronizzato con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante la creazione del banner sul server");
        }
      }
    } catch (error) {
      log("Errore nel salvataggio del banner:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo banner?')) return;

    setIsSaving(true);
    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/delBanner', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ id }),
      });

      const result = await response.json();

      if (result.result) {
        await db.techyshop_banner.delete(id);
        setToast({ open: true, message: 'Banner eliminato con successo!', severity: 'info' });
      } else {
        throw new Error(result.text || "Errore durante l'eliminazione del banner sul server");
      }
    } catch (error) {
      log("Errore nell'eliminazione del banner:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Breadcrumbs & Navigation */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs
          separator={<ChevronRight fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'inherit' }}
            onClick={() => navigate('/techyshop')}
          >
            TechyShop
          </Link>
          <Typography color="primary.main" sx={{ fontWeight: 600 }}>Gestione Banners</Typography>
        </Breadcrumbs>

        <IconButton color="primary" onClick={() => navigate('/techyshop')} sx={{ border: '1px solid', borderColor: 'primary.main' }}>
          <ViewCarousel />
        </IconButton>
      </Box>

      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
            Gestione <Box component="span" sx={{ color: 'primary.main' }}>Banners</Box>
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            In questa sezione potrai gestire i banner pubblicitari e le promozioni visualizzate sullo shop.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<Sync />} 
            onClick={handleSync}
            disabled={isSaving}
          >
            Sincronizza
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<Add />} 
            onClick={() => handleOpenModal()}
            disabled={isSaving}
          >
            Nuovo Banner
          </Button>
        </Box>
      </Box>

      {/* Banners Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Titolo</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Posizione</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Stato</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banners?.map((b) => (
              <TableRow 
                key={b.id} 
                hover 
                onClick={() => handleOpenModal(b)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } 
                }}
              >
                <TableCell sx={{ color: 'white' }}>{b.id}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{b.titolo}</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{b.posizione}</TableCell>
                <TableCell align="center">
                  <Tooltip title={b.attivo === 1 || b.attivo === true ? "Attivo" : "Non Attivo"}>
                    {b.attivo === 1 || b.attivo === true ? <CheckCircle style={{ color: '#4dab56' }} fontSize="small" /> : <Cancel style={{ color: '#f44336' }} fontSize="small" />}
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={(e) => { e.stopPropagation(); handleOpenModal(b); }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={(e) => { e.stopPropagation(); handleDeleteBanner(b.id); }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!banners || banners.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Nessun banner trovato.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Aggiungi/Modifica Banner */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {selectedBanner ? 'Modifica Banner' : 'Aggiungi Nuovo Banner'}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleCloseModal} aria-label="close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Titolo"
                name="titolo"
                value={formData.titolo}
                onChange={handleInputChange}
                disabled={isSaving}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sottotitolo"
                name="sottotitolo"
                value={formData.sottotitolo}
                onChange={handleInputChange}
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12}>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleFileChange}
              />
              <TextField
                fullWidth
                label="URL Immagine"
                name="immagine"
                value={formData.immagine}
                onChange={handleInputChange}
                disabled={isSaving}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CloudUpload />}
                        onClick={handleUploadClick}
                        disabled={isSaving}
                      >
                        Seleziona
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Link"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Testo Bottone"
                name="testo_bottone"
                value={formData.testo_bottone}
                onChange={handleInputChange}
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Posizione"
                name="posizione"
                value={formData.posizione}
                onChange={handleInputChange}
                disabled={isSaving}
                placeholder="es: home_top, sidebar, slider"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Ordinamento"
                name="ordinamento"
                value={formData.ordinamento}
                onChange={handleInputChange}
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Inizio Validità"
                name="data_inizio"
                value={formData.data_inizio}
                onChange={handleInputChange}
                disabled={isSaving}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Fine Validità"
                name="data_fine"
                value={formData.data_fine}
                onChange={handleInputChange}
                disabled={isSaving}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch 
                    name="attivo" 
                    checked={formData.attivo} 
                    onChange={handleInputChange} 
                    color="primary" 
                  />
                }
                label="Banner Attivo"
                disabled={isSaving}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={isSaving}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveBanner} 
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mt: 8 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
          Sistema integrato TechySumo • Ultimo aggiornamento: Marzo 2026
        </Typography>
      </Box>
    </Container>
  );
}

export default TechyShopBanners;
